import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { Point2D, Point3D } from '@paragon/geometry-library';

import { useAppSelector } from '../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../library/layout-geometry/coordinates';
import {
  SnapMousePositionResult,
  usePointPickingPointSnapper,
} from '../../library/layout-snapping/point-snapping/snapMousePosition';
import { getPickingObjectInfo, getPointPickingOtherPoint } from '../../store/selectors/pickingSelectors';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';

interface PointPickingSnapContextValue {
  snapResult: SnapMousePositionResult | null;
  sourcePoint: Point2D | null;
  snap: (point: Point2D) => SnapMousePositionResult;
}

const PointPickingSnapContext = createContext<PointPickingSnapContextValue | null>(null);

export function usePointPickingSnapContext(): PointPickingSnapContextValue | null {
  return useContext(PointPickingSnapContext);
}

interface MousePositionForSession {
  readonly sessionKey: string;
  readonly position: Point3D;
}

export function PointPickingSnapProvider(props: { children: React.ReactNode }) {
  const snap = usePointPickingPointSnapper();
  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);
  const otherPoint = useAppSelector(getPointPickingOtherPoint);
  const pendingPickedPointMovement = useAppSelector((state) => state.application.pendingPickedPointMovement);

  // The mouse position is invalidated whenever the picking session changes (target or fixed endpoint), so that the
  //   snap visual and any consumer previews stay hidden until the user actually moves the mouse during the new
  //   session. Without this, finishing an insertion via keyboard and auto-repeating would render at the stale last
  //   known cursor position.
  const sessionKey =
    pickingObjectInfo?.type === 'pickingLayoutPoint'
      ? `${pickingObjectInfo.target}:${otherPoint?.x ?? '_'}:${otherPoint?.y ?? '_'}`
      : null;

  const [mousePositionForSession, setMousePositionForSession] = useState<MousePositionForSession | null>(null);

  const isPickedPointPending = pendingPickedPointMovement != null;

  useEffect(() => {
    if (sessionKey == null || isPickedPointPending) {
      setMousePositionForSession(null);
      return;
    }

    const currentSessionKey = sessionKey;

    function onMouseMove(event: GlobalThreeJSDomObjectEvent) {
      setMousePositionForSession({
        sessionKey: currentSessionKey,
        position: Point3D.fromObject(event.eventPosition.worldPosition),
      });
    }

    function onMouseLeave() {
      setMousePositionForSession(null);
    }

    threeJsDom.addGlobalListener('mousemove', onMouseMove);
    threeJsDom.addGlobalListener('mouseleave', onMouseLeave);

    return () => {
      threeJsDom.removeGlobalListener('mousemove', onMouseMove);
      threeJsDom.removeGlobalListener('mouseleave', onMouseLeave);
    };
  }, [sessionKey, isPickedPointPending]);

  const mousePosition = mousePositionForSession?.sessionKey === sessionKey ? mousePositionForSession.position : null;

  const value = useMemo((): PointPickingSnapContextValue => {
    if (pendingPickedPointMovement != null) {
      const point = Point2D.fromObject(pendingPickedPointMovement.point);
      return { snapResult: { type: pendingPickedPointMovement.snapType, point }, sourcePoint: point, snap };
    }

    if (mousePosition == null) {
      return { snapResult: null, sourcePoint: null, snap };
    }

    const sourcePoint = convertFromDesignToLayoutCoordinates(mousePosition).projectTo2D();
    const snapResult = snap(sourcePoint);

    return { snapResult, sourcePoint, snap };
  }, [pendingPickedPointMovement, mousePosition, snap]);

  return <PointPickingSnapContext.Provider value={value}>{props.children}</PointPickingSnapContext.Provider>;
}
